
import Axios from 'axios'
import {
  CustomFieldIdByLabel,
  XsrfResilient
} from './Helpers'

import {
  BOPUS_STORE_FIELD,
  BOPUS_FIELD,
} from './Configuration'

// [Gift Boxing, Reserve at Store, Gift Card] should be excluded from inventory check since they are not physical quantities.
const SKUS_ALWAYS_AVAILABLE = ['990000007', '999999250', '999999936']

const groupBy = functor => collection => collection.reduce((hash, item) => { const key = functor(item); return { ...hash, [key]: (hash[key] || []).concat(item) } }, {})
const keys = (...ks) => item => ks.map(k => item[k])
const shipmentGrouper = groupBy(keys('address1', 'isFurniture'))

const wrap = field => collection => collection.map(element => ({ [field]: element }))
let furnitureItems = []
let nonFurnitureItems = []

const Checkout = {
  async settings() {
    if (Checkout.__settings) {
      return Checkout.__settings
    }
    Checkout.__settings = (await Axios.get(`/api/storefront/checkout-settings`, {
      headers: {
        accept: 'application/vnd.bc.v1+json',
        'X-API-INTERNAL': 'This API endpoint is for internal use only and may change in the future'
      }
    })).data
    return Checkout.__settings
  },
  get: async id => {
    const checkout = (await Axios.get(`/api/storefront/checkouts/${id}/?include=consignments.availableShippingOptions`)).data
    return {
      cart: checkout.cart,
      consignments: checkout.consignments || []
    }
  },
  async customFieldIds() {
    if (Checkout.__customFieldIds) {
      return Checkout.__customFieldIds
    }
    const checkoutSettings = await Checkout.settings()
    const fields = checkoutSettings.storeConfig.formFields.shippingAddressFields
    const bopus = CustomFieldIdByLabel({ fields, label: 'BOPUS' })
    const shipToStore = CustomFieldIdByLabel({ fields, label: 'ShipToStoreID' })
    Checkout.__customFieldIds = { bopus, shipToStore }
    return { bopus, shipToStore }
  },

  //TO-DO: move to checkout JS later
  getCheckout: XsrfResilient(async ({ cartId, include }) => {
    return !!cartId && (await Axios.get(`/api/storefront/checkouts/${cartId}?include=consignments.availableShippingOptions`, include?.length ? { params: { include: include.join(',') } } : undefined)).data
  }),
  //TO-DO: move to checkout JS later
  createOrUpdateShipments: XsrfResilient(async ({ checkoutId, addresses, items, user, shipmentIds }) => {
    // This is all complete insanity we try to abstract out from BigCommerce
    //   having a pretty ugly PHP API...
    const isUpdate = !!shipmentIds && shipmentIds.length > 0  // NEW: determine whether to update or create new consignments
    const { availabilityMessage } = items[0]
    const addressesToGroup = addresses.length > 1
      ? addresses
      : items.map(({ id: itemID, isFurniture }) => ({ ...addresses[0], itemID, isFurniture }))
    const grouped = shipmentGrouper(addressesToGroup)

    const shipmentsBodies = Object.keys(grouped).map((address, index) => (
      {
        id: isUpdate ? shipmentIds[index] : address.split(',')[0],
        shippingAddress: { ...grouped[address][0], ...user },
        lineItems: grouped[address].map(shipment => {
          const quantity = items.find(item => item.id === shipment.itemID).quantity
          return { itemId: shipment.itemID, quantity }
        })
      }))

    let shipmentResponse
    // This is the actual call...

    if (isUpdate) {
      for (const shipmentIndex in shipmentsBodies) {
        const shipmentData = shipmentsBodies[shipmentIndex]
        shipmentResponse = (await Axios.put(`/api/storefront/checkouts/${checkoutId}/consignments/${shipmentData.id}?include=consignments.availableShippingOptions`, shipmentData)).data
      }
    } else {
      shipmentResponse = (await Axios.post(`/api/storefront/checkouts/${checkoutId}/consignments?include=consignments.availableShippingOptions`, shipmentsBodies)).data
    }

    const { consignments, grandTotal, subtotal, taxTotal } = shipmentResponse

    // We have to post-process the body, since the response format is dissimilar.
    const shipments = consignments.map(({ id, availableShippingOptions, lineItemIds, shippingAddress }) => ({
      id,
      availableShippingOptions,
      lineItems: lineItemIds.map(itemId => ({
        itemId,
        quantity: items.find(item => item.id === itemId).quantity
      })),
      shippingAddress,
      availabilityMessage
    }))
    return { shipments, grandTotal, subtotal, taxTotal }
  }),

  updateShippingMethod: XsrfResilient(async ({ checkoutId, shipmentId, shippingOptionId }) => {
    return (await Axios.put(
      `/api/storefront/checkouts/${checkoutId}/consignments/${shipmentId}?include=consignments.availableShippingOptions`,
      { shippingOptionId }
    )).data
  }),

  addBillingAddress: XsrfResilient(async ({ checkoutId, user, billingAddress: {
    address1,
    address2 = '',
    city,
    company = '',
    // country='United States',
    countryCode = 'US',
    email,
    firstName,
    lastName,
    phone,
    postalCode,
    shouldSaveAddress = true,
    // FIXME
    // stateOrProvince='Texas',
    stateOrProvinceCode
  } }) => {
    return (await Axios.post(
      `/api/storefront/checkouts/${checkoutId}/billing-address`,
      {
        ...user,
        address1,
        address2,
        city,
        phone,
        email,
        firstName,
        lastName,
        company,
        countryCode,
        postalCode,
        shouldSaveAddress,
        stateOrProvinceCode
      }
    )).data
  }),

  async getCheckoutWithPayments({ cartId, checkoutId }) {
    cartId = checkoutId || cartId
    const include = [
      'cart.lineItems.physicalItems.options',
      'cart.lineItems.digitalItems.options',
      'customer',
      'customer.customerGroup',
      'payments',
      'promotions.banners'
    ]
    return await Checkout.getCheckout({ cartId, include })
  },
  async getOrderWithPayments({ orderId }) {
    const include = [
      'payments',
      'lineItems.physicalItems.socialMedia',
      'lineItems.physicalItems.options',
      'lineItems.digitalItems.socialMedia',
      'lineItems.digitalItems.options'
    ]
    return await Checkout.getOrder({ orderId, include })
  },
  createConsignments: XsrfResilient(async (checkoutId, data) => {
    let consignmentsData
    for (const dataIndex in data) {
      const requestBody = [data[dataIndex]]
      consignmentsData = (await Axios.post(`/api/storefront/checkouts/${checkoutId}/consignments?include=consignments.availableShippingOptions`, requestBody)).data
    }
    return consignmentsData
  }),

  updateConsignments: XsrfResilient(async (checkoutId, data, consignmentId) => {
    try {
      let consignmentsData
      for (const dataIndex in data) {
        const requestBody = data[dataIndex]
        consignmentsData = (await Axios.put(`/api/storefront/checkouts/${checkoutId}/consignments/${consignmentId}?include=consignments.availableShippingOptions`, requestBody)).data
      }
      return consignmentsData
    } catch {
    }
  }),

  deleteConsignment: XsrfResilient(async (checkoutId, consignmentId) => {
    try {
      return (await Axios.delete(`/api/storefront/checkouts/${checkoutId}/consignments/${consignmentId}`)).data
    }
    catch {
    }
  }),
  getOrder: XsrfResilient(async ({ orderId, include }) => {
    const options = include?.length ? { params: { include: include.join(',') } } : undefined
    return (await Axios.get(`/api/storefront/orders/${orderId}`, options)).data
  }),

  BOPUS_FIELD: BOPUS_FIELD,
  BOPUS_STORE_FIELD: BOPUS_STORE_FIELD,

  isBopusConsignment: customFields => !!customFields?.find(({fieldId, fieldValue}) => fieldId === Checkout.BOPUS_FIELD && fieldValue === '1'),

  checkForEmptyConsignments: async checkoutId => {
    const { consignments } = await Checkout.getCheckout({ cartId: checkoutId });
    consignments.forEach(async consignment => {
      if (!!consignment?.lineItemIds)
        await Checkout.deleteConsignment(checkoutId, consignment.id);
    });
  }

}
export default Checkout
